import React from 'react'
import { graphql, useFragment } from 'react-relay'

import { YStack, XStack, Stack } from 'tamagui'
import {
  Grid,
  Image,
  H1,
  Card,
  FormattedMarkdown,
  SponsorsSection,
  Paragraph,
} from '@newComponents'

import { EventScreenNewCoverPhoto } from './EventScreenNewCoverPhoto'
import { EventScreenSettingsButton } from './EventScreenSettingsButton'
import { EventScreenDescription } from './EventScreenDescription'
import { EventScreenOwner } from './EventScreenOwner'
import { EventScreenDateTime } from './EventScreenDateTime'
import { EventScreenJoin } from './EventScreenJoin'
import { EventScreenAttendCard } from './EventScreenAttendCard'
import { EventScreenMap } from './EventScreenMap'
import { EventScreenPresentations } from './EventScreenPresentations'
import { EventHostInvitationBanner } from './EventHostInvitationBanner'

import { EventScreenNewMobile_event$key } from './__generated__/EventScreenNewMobile_event.graphql'
import { EventScreenNewMobile_query$key } from './__generated__/EventScreenNewMobile_query.graphql'

export const EventScreenNewMobile = ({
  event,
  query,
}: {
  event: EventScreenNewMobile_event$key
  query: EventScreenNewMobile_query$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenNewMobile_event on Event {
        name
        description

        prettyUrl

        startAt
        endAt

        hasVenue
        hasExternalUrl

        seoVenue {
          address {
            city
            stateProvinceRegionCode
            zipPostalCode
            countryCode
          }
        }

        owner {
          __typename
          ... on Guild {
            sponsorshipDisplay
          }
        }

        ...EventScreenNewCoverPhoto_event
        ...EventScreenDescription_event
        ...EventScreenSettingsButton_event
        ...EventScreenDateTime_event
        ...EventScreenOwner_event
        ...EventScreenJoin_event
        ...EventScreenPresentations_event
        ...EventScreenAttendCard_event
        ...EventScreenMap_event
        ...EventHostInvitationBanner_event
      }
    `,
    event
  )

  const queryData = useFragment(
    graphql`
      fragment EventScreenNewMobile_query on Query {
        ...SponsorsSection_query
        ...EventScreenAttendCard_query
        ...EventScreenJoin_query
        ...EventHostInvitationBanner_query
      }
    `,
    query
  )

  return (
    <YStack gap='$8' display='flex' $gtSm={{ display: 'none' }}>
      <XStack
        justifyContent='center'
        marginLeft={-20}
        marginRight={-20}
        $gtXs={{ marginLeft: 0, marginRight: 0 }}
      >
        <EventScreenNewCoverPhoto event={data} />
      </XStack>

      <YStack gap='$4'>
        <EventHostInvitationBanner event={data} query={queryData} />

        <H1
          size='$8'
          paddingLeft={0}
          $gtXs={{
            paddingLeft: '$6',
          }}
        >
          {data.name}
        </H1>

        <EventScreenSettingsButton event={data} />

        <Stack
          flexDirection='column'
          gap='$6'
          $gtXs={{ flexDirection: 'row', gap: '$12' }}
        >
          <YStack
            gap='$6'
            paddingLeft={0}
            $gtXs={{
              paddingLeft: '$5',
              flex: 1,
              flexBasis: 0,
            }}
          >
            <EventScreenOwner event={data} />

            <EventScreenDateTime event={data} />

            <EventScreenJoin event={data} query={queryData} />
          </YStack>

          <YStack
            $gtXs={{
              justifyContent: 'center',
              flex: 1,
              flexBasis: 0,
              maxWidth: 300,
            }}
          >
            <EventScreenAttendCard event={data} query={queryData} />
          </YStack>
        </Stack>
      </YStack>

      <YStack gap='$8'>
        <EventScreenDescription event={data} />

        <EventScreenPresentations event={data} />

        <EventScreenMap event={data} />
      </YStack>

      {data.owner?.__typename !== 'Guild' || data.owner.sponsorshipDisplay ? (
        <SponsorsSection query={queryData} padding='$4' />
      ) : null}
    </YStack>
  )
}
